import { queryOptions } from "@tanstack/react-query";
import { adaptConfiguratorConfiguration } from "./adaptConfiguratorConfiguration";
import { initializeConfiguratorV2 } from "./configuratorV2Api";
import type { ConfiguratorConfiguration } from "../contract/configuration";
import type { ConfiguratorRuntimeContext } from "../contract/context";
import type { ConfiguratorV2ContextDTO } from "./configuratorV2Api.types";

export interface ConfiguratorRuntimeQueryData {
  configuration: ConfiguratorConfiguration;
  context: ConfiguratorRuntimeContext;
  initialExpansion: ConfiguratorInitialExpansion;
}

export interface ConfiguratorInitialExpansion {
  mode: "start" | "load";
  revision: number;
}

let initialExpansionRevision = 0;
const INITIAL_EXPANSION_STORAGE_PREFIX = "configurator-v2.initial-expansion";

export const createConfiguratorInitialExpansion = (
  mode: ConfiguratorInitialExpansion["mode"],
): ConfiguratorInitialExpansion => ({
  mode,
  revision: ++initialExpansionRevision,
});

export const markNextConfiguratorInitialExpansion = (
  productCode: string,
  mode: ConfiguratorInitialExpansion["mode"],
) => {
  window.sessionStorage.setItem(`${INITIAL_EXPANSION_STORAGE_PREFIX}.${productCode}`, mode);
};

export const consumeNextConfiguratorInitialExpansion = (
  productCode: string,
): ConfiguratorInitialExpansion["mode"] | null => {
  const key = `${INITIAL_EXPANSION_STORAGE_PREFIX}.${productCode}`;
  const mode = window.sessionStorage.getItem(key);
  window.sessionStorage.removeItem(key);
  return mode === "load" || mode === "start" ? mode : null;
};

export const adaptConfiguratorRuntimeContext = (context: ConfiguratorV2ContextDTO): ConfiguratorRuntimeContext => ({
  helpUrl: context.helpUrl?.trim() || null,
  saveMode: context.saveMode,
  customization: {
    ...context.customization,
    specialistLinkURL: context.customization.specialistLinkURL?.trim() || undefined,
  },
});

export const configuratorConfigurationKey = (productCode: string) =>
  ["configurator-configuration", productCode] as const;

export const configuratorMutationKey = (productCode: string) => ["configurator-mutation", productCode] as const;

export const configuratorConfigurationQueryOptions = (productCode: string) =>
  queryOptions({
    queryKey: configuratorConfigurationKey(productCode),
    queryFn: async () => {
      const response = await initializeConfiguratorV2({ productCode });
      const initialExpansionMode = consumeNextConfiguratorInitialExpansion(productCode) ?? "start";
      return {
        configuration: {
          ...adaptConfiguratorConfiguration(response.data),
          helpUrl: response.context.helpUrl?.trim() || null,
        },
        context: adaptConfiguratorRuntimeContext(response.context),
        initialExpansion: createConfiguratorInitialExpansion(initialExpansionMode),
      };
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });
